import React from 'react'
import { gql, useQuery } from '@apollo/client'
import { useParams } from 'react-router-dom'
import VariantPage from './VariantPage'
import NotFoundPageContent from '@/components/pages/NotFoundPage/NotFoundPageContent'
import GeneralError from '@/components/common/GeneralError'
import LoadingSpinner from '@/components/common/LoadingSpinner'

const HEALTHY_DATASET_ID = '17652777241484514962'
const LUNG_CANCER_DATASET_ID = '17661357189059001598'

const query = gql`
    query VariantDetail($variantId: String!, $healthyDatasetId: String!, $lungCancerDatasetId: String!) {
        healthyVariant: variant(variant_id: $variantId, dataset_id: $healthyDatasetId) {
            variant_id
            pos
            ref
            alt
            length
            population_frequencies {
                population_source
                populations {
                    population_subtype
                    variant_id
                    allele_stats {
                        allele_count
                        allele_number
                        allele_frequency
                    }
                }
            }
            
            coverage_exome {
                mean
                pos
            }
            coverage_genome {
                mean
                pos
            }
            rsids
            transcript_consequence {
                transcript_id
                consequence
                hgvsc
            }
            gene_ids
        }
        lungCancerVariant: variant(variant_id: $variantId, dataset_id: $lungCancerDatasetId) {
            variant_id
            pos
            ref
            alt
            length
            population_frequencies {
                population_source
                populations {
                    population_subtype
                    variant_id
                    allele_stats {
                        allele_count
                        allele_number
                        allele_frequency
                    }
                }
            }
            
            coverage_exome {
                mean
                pos
            }
            coverage_genome {
                mean
                pos
            }
            rsids
            transcript_consequence {
                transcript_id
                consequence
                hgvsc
            }
            gene_ids
        }
        healthyDataset: dataset(dataset_id: $healthyDatasetId) {
            dataset_id
            dataset_name
        }
        lungCancerDataset: dataset(dataset_id: $lungCancerDatasetId) {
            dataset_id
            dataset_name
        }
    }
`

type Params = {
    variantId: string
}

export default function VariantPageContainer() {
    const { variantId = '' } = useParams<Params>()

    const { loading, error, data } = useQuery(query, {
        variables: {
            variantId,
            healthyDatasetId: HEALTHY_DATASET_ID,
            lungCancerDatasetId: LUNG_CANCER_DATASET_ID
        }
    })

    if (error) {
        return <GeneralError />
    }

    if (loading) {
        return <LoadingSpinner message='Loading variant' />
    }

    // Check if at least one variant exists
    const hasAtLeastOneVariant = data?.healthyVariant || data?.lungCancerVariant

    if (!hasAtLeastOneVariant) {
        return <NotFoundPageContent message='Variant not found' />
    }

    // Use the variant that exists, prefer healthy dataset for main display
    const primaryVariant = data.healthyVariant || data.lungCancerVariant

    return (
        <VariantPage
            variant={primaryVariant}
            healthyVariant={data.healthyVariant}
            lungCancerVariant={data.lungCancerVariant}
            healthyDataset={data.healthyDataset}
            lungCancerDataset={data.lungCancerDataset}
        />
    )
}
